import { makeid } from 'utils/helpers/makeid';
import { DateFormatter } from 'infrastructure/functions';

import ScriptWrapper from './script-wrapper';
import { addField, formatList, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createMobileAppTemplate = (data: ITemplateProps) => {
  const { userFullname } = data;
  return [
    {
      key: makeid(10),
      name: 'greetingComplianceHipaaVerification',
      type: 'checkbox',
      title: <p>Greeting & Compliance / HIPAA Verification</p>,
      description: (
        <ScriptWrapper>
          "Hello, this is {userFullname} with Tellihealth. For quality and
          compliance purposes, this call may be recorded. To protect your
          privacy, can you please confirm your full name and date of birth?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient identity verified (name + DOB)',
          value: 'patientIdentityVerified',
        },
        {
          key: makeid(10),
          label: 'Caregiver present → Consent confirmed',
          value: 'caregiverPresentConsentConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Are you the patient's caregiver? Do I have the patient's
                  consent to speak with you today?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Recorded line notification provided',
          value: 'recordedLineNotificationProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'readingAdherenceBarrierCheck',
      type: 'radio',
      title: <p>Reading Adherence & Barrier Check</p>,
      description: (
        <ScriptWrapper>
          "I’m calling to check in and help with the mobile app." "Have you been
          able to take and send your readings, or has anything been getting in
          the way?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Consistent readings',
          value: 'consistentReadings',
        },
        {
          key: makeid(10),
          label: 'Inconsistent readings',
          value: 'inconsistentReadings',
        },
        {
          key: makeid(10),
          label: 'No readings',
          value: 'noReadings',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know — I’ll document that so we can
                  address it."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Other',
          value: 'other',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'textarea',
              name: 'readingAdherenceCheckOther',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Barriers identified',
          value: 'barriersIdentified',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know — I’ll document that so we can
                  support you."
                </ScriptWrapper>
              ),
            },
            {
              key: makeid(10),
              name: 'barriersList',
              type: 'checkbox',
              title: <p>Barriers</p>,
              description: '',
              required: false,
              closable: true,
              options: [
                {
                  key: makeid(10),
                  label: 'Device',
                  value: 'device',
                },
                {
                  key: makeid(10),
                  label: 'Supplies',
                  value: 'supplies',
                },
                {
                  key: makeid(10),
                  label: 'Schedule',
                  value: 'schedule',
                },
                {
                  key: makeid(10),
                  label: 'Education',
                  value: 'education',
                },
                {
                  key: makeid(10),
                  label: 'Connectivity',
                  value: 'connectivity',
                },
                {
                  key: makeid(10),
                  label: 'Power/charging',
                  value: 'powerCharging',
                },
                {
                  key: makeid(10),
                  label: 'Other',
                  value: 'other',
                  hideSubitemsIfParentNotSelected: true,
                  subItems: [
                    {
                      key: makeid(10),
                      type: 'textarea',
                      name: 'barriersListOther',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'mobileAppUseStatus',
      type: 'radio',
      title: <p>Mobile App Use Status</p>,
      description: (
        <ScriptWrapper>
          "The mobile app is designed to make it easier to view your readings,
          receive updates, and stay connected to your RPM care."
          <br />
          "You don't have to use the app for your readings to count, but many
          patients find it helpful."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'App downloaded',
          value: 'appDownloaded',
        },
        {
          key: makeid(10),
          label: 'App not downloaded',
          value: 'appNotDownloaded',
        },
        {
          key: makeid(10),
          label: 'Patient declined app use',
          value: 'patientDeclinedAppUse',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'appAccessFunctionality',
      type: 'checkbox',
      title: <p>App Access & Functionality</p>,
      description: (
        <ScriptWrapper>
          "Do you currently have the app downloaded on your phone?"
          <br />
          If NO: "That's okay — I can help guide you through downloading it if
          you'd like."
          <br />
          If YES: "Great. Are you able to log in without issues?"
          <br />
          "With the app, you can:"
          <br />
          – View recent and past readings
          <br />
          – See reminders or messages related to your RPM care
          <br />– Stay informed between check-ins "Are there any features you
          have questions about?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Able to log in',
          value: 'ableToLogIn',
        },
        {
          key: makeid(10),
          label: 'Login issues',
          value: 'loginIssues',
        },
        {
          key: makeid(10),
          label: 'App features reviewed',
          value: 'appFeaturesReviewed',
        },
        {
          key: makeid(10),
          label: 'Questions answered',
          value: 'questionsAnswered',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'troubleshootingEducationProvided',
      type: 'checkbox',
      title: <p>Troubleshooting / Education Provided</p>,
      description: (
        <ScriptWrapper>
          "If you're having trouble with the app, we can look at things like:"
          <br />
          – Login or password issues
          <br />
          – App updates
          <br />
          – Notifications not coming through
          <br />
          "We can also document concerns and follow up if needed."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Login or password support',
          value: 'loginOrPasswordSupport',
        },
        {
          key: makeid(10),
          label: 'Notification settings reviewed',
          value: 'notificationSettingsReviewed',
        },
        {
          key: makeid(10),
          label: 'App update guidance provided',
          value: 'appUpdateGuidanceProvided',
        },
        {
          key: makeid(10),
          label: 'Follow-up required',
          value: 'followUpRequired',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'insuranceVerificationOnFile',
      type: 'radio',
      title: <p>Insurance Verification (On File Only)</p>,
      description: (
        <ScriptWrapper>
          "I want to confirm the type of insurance we have on file for you."
          <br />
          "What type of insurance do you have listed with your clinic: Medicare,
          Medicare Advantage, or Commercial?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Medicare',
          value: 'medicare',
        },
        {
          key: makeid(10),
          label: 'Medicare Advantage (plan selected)',
          value: 'medicareAdvantage',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Which plan is listed?"
                  <br />
                  "Thank you — I'll confirm what's documented in your chart."
                </ScriptWrapper>
              ),
            },
            {
              key: makeid(10),
              type: 'checkbox',
              name: 'insuranceTypeOnFile',
              options: [
                {
                  key: makeid(10),
                  label: 'Insurance type on file confirmed',
                  value: 'insuranceTypeOnFileConfirmed',
                },
              ],
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Commercial',
          value: 'commercial',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'cellPhoneVerification',
      type: 'radio',
      title: <p>Cell Phone Verification</p>,
      description: (
        <ScriptWrapper>
          "Before we continue, I want to confirm the best cell phone number to
          reach you. Is [number on file] still correct?"
          <br />
          If updated or no cell phone: acknowledge and document.
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Cell phone confirmed',
          value: 'cellPhoneConfirmed',
        },
        {
          key: makeid(10),
          label: 'Updated',
          value: 'updated',
        },
        {
          key: makeid(10),
          label: 'No cell phone',
          value: 'noCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'textingEnrollment',
      type: 'radio',
      title: <p>Texting Enrollment</p>,
      description: (
        <ScriptWrapper>
          If patient has a cell phone: "We also offer text messages for
          reminders and updates related to your RPM care. Would you like to
          receive text messages from us?"
          <br />
          If yes: enroll. If no: document decline. If no cell phone: do not
          offer texting.
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Already enrolled',
          value: 'alreadyEnrolled',
        },
        {
          key: makeid(10),
          label: 'Offered/Accepted',
          value: 'offeredAccepted',
        },
        {
          key: makeid(10),
          label: 'Offered/Declined',
          value: 'offeredDeclined',
        },
        {
          key: makeid(10),
          label: 'Not eligible',
          value: 'notEligible',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionRequirement',
      type: 'checkbox',
      title: <p>Monthly Interaction Requirement + Scheduling</p>,
      description: (
        <ScriptWrapper>
          “As a reminder, these monthly phone calls are required to remain
          active in the RPM program.”
          <br />
          “They help us stay connected and identify issues early.”
          <br />
          “Would you like to schedule your next monthly check-in now, or should
          we call you closer to the date?”
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Monthly phone call requirement reviewed',
          value: 'monthlyPhoneCallRequirementReviewed',
        },
        {
          key: makeid(10),
          label: 'Program monitoring process explained',
          value: 'programMonitoringProcessExplained',
        },
        {
          key: makeid(10),
          label: 'Nurse or clinic outreach for out-of-range readings explained',
          value: 'nurseOrClinicOutreachForOutOfRangeReadingsExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionScheduling',
      title: <p>Monthly Interaction Scheduling</p>,
      type: 'radio',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'First monthly call scheduled (date)',
          value: 'nextMonthlyCheckInScheduled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'input-date',
              required: false,
              name: 'nextMonthlyCheckInScheduledDate',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Patient declined scheduling',
          value: 'patientDeclinedScheduling',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingAdditionalQuestions',
      type: 'radio',
      title: <p>Closing - Additional Questions</p>,
      description: (
        <ScriptWrapper>
          "Just to confirm, do you feel you understand how the mobile app fits
          into your RPM care?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Had no additional questions',
          value: 'noAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Great — thank you."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Asked questions — addressed during call',
          value: 'hasAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>"Does that answer your question?"</ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingUnderstanding',
      type: 'radio',
      title: <p>Closing - Understanding</p>,
      description: (
        <ScriptWrapper>
          "Just to confirm, do you feel you understand how the mobile app fits
          into your RPM care?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Verbalized full understanding',
          value: 'full',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Perfect."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Verbalized partial understanding',
          value: 'partial',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know. Is there anything I can
                  clarify before we finish?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Unable to verbalize understanding',
          value: 'unable',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you — I'll document that and we can follow up as
                  needed."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: '_empty',
      type: 'empty',
      title: <p>Final close</p>,
      description: (
        <ScriptWrapper>
          "Thank you for your time today. Please feel free to reach out if you
          need additional support. Take care."
        </ScriptWrapper>
      ),
    },
  ] as const satisfies TFormTemplateItem[];
};

export type TMobileAppTemplateValues = ExtractStepValues<
  ReturnType<typeof createMobileAppTemplate>
>;

export const createMobileAppBlurb = (
  data: TMobileAppTemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingComplianceHipaaVerification,
    readingAdherenceBarrierCheck,
    readingAdherenceCheckOther,
    barriersList,
    barriersListOther,
    mobileAppUseStatus,
    appAccessFunctionality,
    troubleshootingEducationProvided,
    insuranceVerificationOnFile,
    cellPhoneVerification,
    textingEnrollment,
    monthlyInteractionRequirement,
    monthlyInteractionScheduling,
    nextMonthlyCheckInScheduledDate,
    closingAdditionalQuestions,
    closingUnderstanding,
  } = data;

  const txt: string[] = [];

  if (
    greetingComplianceHipaaVerification?.length &&
    !hasField(ctx, 'greetingComplianceHipaaVerification')
  ) {
    addField(ctx, 'greetingComplianceHipaaVerification');
    const map = {
      patientIdentityVerified:
        'Greeting and compliance completed. Patient identity verified via name and date of birth.',
      recordedLineNotificationProvided:
        'Patient notified call may be recorded for quality and compliance purposes.',
      caregiverPresentConsentConfirmed:
        'Caregiver participation confirmed and patient consent obtained.',
    };
    txt.push(...mapValuesToText(greetingComplianceHipaaVerification, map));
  }

  if (
    readingAdherenceBarrierCheck &&
    !hasField(ctx, 'readingAdherenceBarrierCheck')
  ) {
    addField(ctx, 'readingAdherenceBarrierCheck');
    const map = {
      consistentReadings: 'consistent readings',
      inconsistentReadings: 'inconsistent readings',
      missedReadings: 'missed readings',
      noReadings: 'no readings',
      barriersIdentified: 'barriers identified',
      other: readingAdherenceCheckOther,
    };

    const value = map[readingAdherenceBarrierCheck];

    if (value) {
      txt.push(`Reading adherence reviewed. Patient reports ${value}.`);
    }

    if (
      readingAdherenceBarrierCheck === 'barriersIdentified' &&
      barriersList?.length &&
      !hasField(ctx, 'barriersList')
    ) {
      addField(ctx, 'barriersList');
      const barriersMap = {
        device: 'device issues',
        supplies: 'supply availability',
        schedule: 'schedule conflicts',
        education: 'education needs',
        connectivity: 'connectivity issues',
        powerCharging: 'power or charging issues',
        other: barriersListOther,
      };
      const items = mapValuesToText(barriersList, barriersMap);
      if (items.length) {
        txt.push(
          `Barriers identified and documented, including ${formatList(items)}.`,
        );
      }
    }
  }

  if (mobileAppUseStatus && !hasField(ctx, 'mobileAppUseStatus')) {
    addField(ctx, 'mobileAppUseStatus');
    const map = {
      appDownloaded: 'downloaded',
      appNotDownloaded: 'not downloaded',
      patientDeclinedAppUse: 'declined',
    };
    txt.push(`Mobile app use reviewed. App ${map[mobileAppUseStatus]}.`);
  }

  if (
    appAccessFunctionality?.length &&
    !hasField(ctx, 'appAccessFunctionality')
  ) {
    addField(ctx, 'appAccessFunctionality');
    const map = {
      ableToLogIn: 'able to log in',
      loginIssues: 'login issues encountered',
      appFeaturesReviewed: 'app features reviewed',
      questionsAnswered: 'patient questions answered',
    };
    const items = mapValuesToText(appAccessFunctionality, map);
    if (items.length) {
      txt.push(
        `App access and functionality reviewed with patient: ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    troubleshootingEducationProvided?.length &&
    !hasField(ctx, 'troubleshootingEducationProvided')
  ) {
    addField(ctx, 'troubleshootingEducationProvided');
    const map = {
      loginOrPasswordSupport: 'login or password support provided',
      notificationSettingsReviewed: 'notification settings reviewed',
      appUpdateGuidanceProvided: 'app update guidance provided',
      followUpRequired: 'follow-up required',
    };
    const items = mapValuesToText(troubleshootingEducationProvided, map);
    if (items.length) {
      txt.push(
        `Mobile app education and troubleshooting provided, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    insuranceVerificationOnFile &&
    !hasField(ctx, 'insuranceVerificationOnFile')
  ) {
    addField(ctx, 'insuranceVerificationOnFile');
    const map = {
      medicare: 'Medicare',
      medicareAdvantage: 'Medicare Advantage (plan selected)',
      commercial: 'Commercial',
    };
    txt.push(
      `Insurance type confirmed as ${map[insuranceVerificationOnFile]} based on information on file.`,
    );
  }

  if (cellPhoneVerification && !hasField(ctx, 'cellPhoneVerification')) {
    addField(ctx, 'cellPhoneVerification');
    const map = {
      cellPhoneConfirmed: 'confirmed',
      updated: 'updated',
      noCellPhone: 'no cell phone',
    };
    txt.push(
      `Cell phone contact reviewed. Number ${map[cellPhoneVerification]}. `,
    );
  }

  if (textingEnrollment && !hasField(ctx, 'textingEnrollment')) {
    addField(ctx, 'textingEnrollment');
    const map = {
      alreadyEnrolled: 'already enrolled',
      offeredAccepted: 'offered and accepted',
      offeredDeclined: 'offered and declined',
      notEligible: 'not eligible—no cell phone',
    };
    txt.push(`Texting enrollment reviewed ${map[textingEnrollment]}.`);
  }

  if (
    monthlyInteractionRequirement?.length &&
    !hasField(ctx, 'monthlyInteractionRequirement')
  ) {
    addField(ctx, 'monthlyInteractionRequirement');
    const map = {
      monthlyPhoneCallRequirementReviewed: 'monthly phone call requirement',
      programMonitoringProcessExplained: 'program monitoring process explained',
      nurseOrClinicOutreachForOutOfRangeReadingsExplained:
        'that out-of-range readings may result in nurse or clinic outreach',
    };
    const items = mapValuesToText(monthlyInteractionRequirement, map);
    if (items.length) {
      txt.push(
        `Monthly interaction requirement reviewed with patient, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    monthlyInteractionScheduling &&
    !hasField(ctx, 'monthlyInteractionScheduling')
  ) {
    addField(ctx, 'monthlyInteractionScheduling');
    const map = {
      nextMonthlyCheckInScheduled: 'scheduled',
      patientDeclinedScheduling: 'not scheduled—patient declined',
    };
    let val = map[monthlyInteractionScheduling];
    if (
      monthlyInteractionScheduling === 'nextMonthlyCheckInScheduled' &&
      nextMonthlyCheckInScheduledDate
    ) {
      val += `in ${DateFormatter({
        date: nextMonthlyCheckInScheduledDate,
      })}`;
    }

    txt.push(`First monthly check-in ${val}.`);
  }

  if (
    closingAdditionalQuestions &&
    !hasField(ctx, 'closingAdditionalQuestions')
  ) {
    addField(ctx, 'closingAdditionalQuestions');

    const map = {
      noAdditionalQuestions: 'had no additional questions',
      hasAdditionalQuestions:
        'asked questions which were addressed during the call',
    };

    txt.push(
      `Call concluded after confirming patient ${map[closingAdditionalQuestions]}.`,
    );
  }

  if (closingUnderstanding && !hasField(ctx, 'closingUnderstanding')) {
    addField(ctx, 'closingUnderstanding');

    const map = {
      full: 'verbalized full understanding',
      partial: 'verbalized partial understanding',
      unable: 'was unable to verbalize understanding',
    };

    txt.push(
      `Patient ${map[closingUnderstanding]} of the information discussed.`,
    );
  }

  return txt.join();
};
